import React, { useEffect, useState } from 'react';
import {
  loadKeysByWeekByGame,
  exportKeysByWeekByGame,
} from '../../../../actions/reportByWeekActions';
import { Table, SimpleTextFilter, TableColumn, getVisibleColumns } from '../../../common/Table';
import { CommaNumber_NoDecimal, range } from '../../../../util/formatter';
import Loading from '../../../common/Loading';
import KwPanel from '../../../common/KwPanel';
import { useLocalStorage } from '../../../../hooks/useLocalStorage';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../../hooks/useTableSettings';
import YearFormSelect from '../YearFormSelect';
import CompanyFormSelect from '../CompanyFormSelect';
import GameLink from '../../../common/links/GameLink';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { fetchCompanies } from '../../../../reducers/companyReducer';
import { useAppDispatch, useAppSelector } from '../../../../hooks';

export interface KeysByWeekByGameListProps { tableViewSwitcher?: React.ReactNode }
function KeysByWeekByGameList({ tableViewSwitcher }: KeysByWeekByGameListProps) {
  const companies = useAppSelector((state) => state.companies);
  const currentUser = useAppSelector((state) => state.auth);

  const dispatch = useAppDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const allColumns = ['game', 'year'];
  for (let i = 1; i <= 52; i++) {
    allColumns.push(`week${i}`);
  }
  const defaultVisibleColumns = allColumns;
  const [reload, setReload] = useState<Date | undefined>();
  const [items, setItems] = useState<any>();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useLocalStorage('report-year', currentYear);
  const [company, setCompany] = useLocalStorage<string | null>('report-company', null);
  const years = range(currentYear, currentYear - 5, -1);
  const params = new URLSearchParams(location.search);
  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    isLoading,
    parameters,
  } = useTableSettings(
    'keysbyweekgamereport-settings',
    getTableSettingsDefaults({
      defaultSort: 'game',
      defaultVisibleColumns,
    }),
    { year, company },
    loadKeysByWeekByGame,
    setItems,
    reload,
    t('Failed to load report')
  );

  useEffect(() => {
    if (!companies) {
      dispatch(fetchCompanies());
    }
  }, [companies, dispatch]);

  // The base config of the columns, this will be filtered based on the visible (above)
  let columnsConfig: TableColumn[] = [
    {
      Key: 'game',
      Label: t('Game'),
      width: '20%',
      Render: (item) => (
        <GameLink
          gameId={item.gameId}
          gameTitle={item.game}
          currentUser={currentUser}
        />
      ),
      Filter: {
        Render: (col) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'year',
      Label: t('Year'),
      Render: (item) => item.year,
    },
  ];
  // add the remaining 52 weeks to the columnsConfig array
  for (let i = 1; i <= 52; i++) {
    columnsConfig.push({
      Key: `week${i}`,
      Label: i.toString(),
      Render: (row) => CommaNumber_NoDecimal(row[`week${i}`]),
    });
  }
  // filter
  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = Number(e.target.value);
    setYear(newYear);
    parameters.current = { ...parameters.current, year: newYear };
    setReload(new Date());
  };

  const handleCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCompany(e.target.value);
    parameters.current = { ...parameters.current, company: e.target.value };
    setReload(new Date());
  };

  // return an alaska table
  return items ? (
    <>
      <div className="form-horizontal row px-4 px-md-0">
        <div className="col-md-6">
          <YearFormSelect
            year={year}
            years={years}
            handleYearChange={handleYearChange}
          ></YearFormSelect>
        </div>
        <div className="col-md-6">
          <CompanyFormSelect
            company={company}
            companies={companies}
            handleCompanyChange={handleCompanyChange}
          ></CompanyFormSelect>
        </div>
      </div>
      <KwPanel className="mb-4">
        <Table
          className="table-v-stripes"
          columns={columns}
          allColumns={allColumns}
          defaultVisibleColumns={defaultVisibleColumns}
          rows={items.data.map((x: any) => {
            return { Key: x.gameId, ...x };
          })}
          totalRowCount={items.totalRowCount}
          filteredRowCount={items.filteredRowCount}
          tableSettings={tableSettings}
          setTableSettings={setTableSettings}
          isLoading={isLoading}
          filters={filters}
          filterChange={filterChange}
          exportData={(filters, scopes) =>
            exportKeysByWeekByGame(year, company, filters, scopes)
          }
          tableActionBarButtons={tableViewSwitcher}
        />
      </KwPanel>
    </>
  ) : (
    <Loading />
  );
}

export default KeysByWeekByGameList;
